import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { EmployerInvitationPreview, type InvitePerson } from "@/components/cl/EmployerInvitationPreview";
import { getAdminSession } from "@/lib/api-client";
import { fetchAdminCandidates } from "@/lib/admin-api";

type Search = { posted: string; role: string; company: string; email: string; ref: string; location: string; contract: string; pay: string; picks: string };
const str = (v: unknown) => (typeof v === "string" ? v : "");

export const Route = createFileRoute("/invitation-preview")({
  head: () => ({ meta: [{ title: "Invitation preview | Closdd" }, { name: "robots", content: "noindex,nofollow" }] }),
  validateSearch: (s: Record<string, unknown>): Search => ({
    posted: str(s["posted"]),
    role: str(s["role"]),
    company: str(s["company"]),
    email: str(s["email"]),
    ref: str(s["ref"]),
    location: str(s["location"]),
    contract: str(s["contract"]),
    pay: str(s["pay"]),
    picks: str(s["picks"]),
  }),
  component: InvitationPreview,
});

const initialsOf = (name: string) =>
  name.split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join("").toUpperCase() || "?";
const shortName = (name: string) => {
  const [first, ...rest] = name.trim().split(/\s+/);
  return rest.length ? `${first} ${rest[rest.length - 1]![0]}.` : (first ?? "Candidate");
};
const clock = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

function InvitationPreview() {
  const search = Route.useSearch();
  const [state, setState] = useState<"loading" | "denied" | "ready">("loading");
  const [people, setPeople] = useState<InvitePerson[]>([]);

  useEffect(() => {
    let off = false;
    (async () => {
      const admin = await getAdminSession().catch(() => null);
      if (!admin) return !off && setState("denied");
      const all = await fetchAdminCandidates();
      const wanted = search.picks.split(",").filter(Boolean);
      const pool = wanted.length ? wanted.map((id) => all.find((c) => c.id === id)).filter((c) => !!c) : all.filter((c) => c.showcase.audioUrl && !c.demo);
      const list = pool.slice(0, 6).map((c): InvitePerson => {
        const s = c.showcase;
        const seconds = s.audioSeconds || 60;
        return {
          initials: initialsOf(c.name),
          name: shortName(c.name),
          role: c.role || "Salesperson",
          loc: c.location || s.location || "",
          years: c.experienceLevel || "Experience on profile",
          len: clock(seconds),
          seconds,
          headline: s.headline || "Recorded a 60-second pitch for your role. Press play to hear how they sell.",
          facts: [c.experienceLevel, s.availabilityWhen, s.location].filter((x): x is string => Boolean(x)).slice(0, 4),
          ...(s.audioUrl ? { audioUrl: s.audioUrl } : {}),
        };
      });
      if (!off) {
        setPeople(list);
        setState("ready");
      }
    })().catch(() => !off && setState("denied"));
    return () => {
      off = true;
    };
  }, [search.picks]);

  if (state === "loading") return <div style={{ minHeight: "100vh", background: "#F3F3F0" }} aria-busy="true" />;
  if (state === "denied")
    return (
      <div className="cl grid min-h-screen place-items-center bg-[#F3F3F0] px-6 text-center">
        <div>
          <h1 className="text-[32px] font-extrabold tracking-[-0.03em] text-[#0B2160]">Admin preview only.</h1>
          <p className="mt-2.5 text-[17px] text-[#3A3A40]">Log in to the admin to preview an employer invitation.</p>
          <a href="/login" className="mt-6 inline-flex min-h-12 items-center rounded-[4px] bg-[#FF5A3C] px-6 text-[15px] font-extrabold text-[#0B2160]">Log in</a>
        </div>
      </div>
    );

  const ref = search.ref || "role";
  return (
    <EmployerInvitationPreview
      people={people}
      role={{
        company: search.company || "Your company",
        contactEmail: search.email,
        title: search.role || "Sales role",
        location: search.location,
        contract: search.contract,
        pay: search.pay,
        posted: search.posted || "recently",
        inviteLink: `closdd.com/invite/${ref.toLowerCase()}`,
        jobUrl: `/jobs/${encodeURIComponent(ref)}`,
      }}
    />
  );
}
